import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, VolumeX } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { api } from "@/lib/api";

const LOGO_IMG =
  "https://customer-assets-v7afamib.emergentagent.net/job_soundwave-shop-10/artifacts/ol4frpvy_03915719-7346-49A6-A7AA-BE0F4907F16D.PNG";

const DEFAULT_INTRO = {
  id: "intro-movement",
  title: "THE MOVEMENT STARTS HERE",
  album: "SOUTHERN SMOKE MUSIC GROUP — INTRO",
  cover: "https://customer-assets-v7afamib.emergentagent.net/job_soundwave-shop-10/artifacts/3p2bphn4_IMG_3187.PNG",
  audio: `${process.env.REACT_APP_BACKEND_URL}/api/files/millzz/intro/movement-starts-here.mp3`,
};

export default function IntroGate() {
  const [entered, setEntered] = useState(() => sessionStorage.getItem("millzz_entered") === "1");
  const [intro, setIntro] = useState(DEFAULT_INTRO);
  const [ready, setReady] = useState(false);
  const { toggle } = usePlayer();

  useEffect(() => {
    api.get("/settings/intro")
      .then((r) => {
        if (r.data.enabled === false) sessionStorage.setItem("millzz_entered", "1");
        if (r.data.audio) setIntro({ ...DEFAULT_INTRO, audio: r.data.audio, title: r.data.title || DEFAULT_INTRO.title });
        setEntered(sessionStorage.getItem("millzz_entered") === "1");
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  const enter = (withSound) => {
    if (withSound) toggle(intro);
    sessionStorage.setItem("millzz_entered", "1");
    setEntered(true);
  };

  return (
    <AnimatePresence>
      {ready && !entered && (
        <motion.div
          data-testid="intro-gate"
          exit={{ opacity: 0, transition: { duration: 0.8 } }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background px-6"
        >
          <div className="absolute left-1/2 top-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/15 blur-[130px]" />
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="relative font-mono text-xs tracking-[0.5em] text-primary"
          >
            SOUTHERN SMOKE MUSIC GROUP PRESENTS
          </motion.p>
          <motion.img
            src={LOGO_IMG}
            alt="Southern Smoke Music Group"
            data-testid="intro-logo"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative mt-8 w-64 md:w-96"
          />
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="relative mt-12 flex flex-col items-center gap-4 sm:flex-row"
          >
            <button
              data-testid="intro-enter-sound-button"
              onClick={() => enter(true)}
              className="flex items-center gap-3 bg-primary px-8 py-4 font-mono text-sm font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0px_#fff]"
            >
              <Play size={16} /> ENTER WITH SOUND
            </button>
            <button
              data-testid="intro-enter-quiet-button"
              onClick={() => enter(false)}
              className="flex items-center gap-3 border border-white/25 px-8 py-4 font-mono text-sm tracking-[0.2em] text-white/70 transition-colors duration-300 hover:border-primary hover:text-primary"
            >
              <VolumeX size={16} /> ENTER QUIET
            </button>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 0.8 }}
            className="relative mt-10 font-mono text-[10px] tracking-[0.3em] text-white/30"
          >
            SOUND ON RECOMMENDED — "THE MOVEMENT STARTS HERE"
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
